import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const QualityCheckTaskInterface = ({ task, onTaskAction, onComplete, readOnly = false }) => {
  const { t } = useTranslation('operator');
  const [inspectionData, setInspectionData] = useState({
    sampleSize: 5,
    inspectedQuantity: 0,
    passedQuantity: 0,
    failedQuantity: 0,
    overallResult: null, // 'pass' or 'fail'
    notes: ''
  });

  // Quality criteria (could be loaded from task config)
  const qualityCriteria = [
    {
      id: 'visual_defects',
      name: 'Visual Defects',
      description: 'Check for scratches, spots, color variations',
      type: 'pass_fail',
      result: null,
      notes: ''
    },
    {
      id: 'dimensions',
      name: 'Dimensions',
      description: 'Verify dimensions within tolerance ±0.5mm',
      type: 'measurement',
      tolerance: '±0.5mm',
      targetValue: '',
      actualValue: '',
      result: null,
      notes: ''
    },
    {
      id: 'print_quality',
      name: 'Print Quality',
      description: 'Check color accuracy, registration, clarity',
      type: 'pass_fail',
      result: null,
      notes: ''
    },
    {
      id: 'adhesion_test',
      name: 'Adhesion Test',
      description: 'Test adhesive strength (if applicable)',
      type: 'measurement',
      tolerance: '>5N/25mm',
      targetValue: '5.0',
      actualValue: '',
      result: null,
      notes: ''
    },
    {
      id: 'die_cutting_accuracy',
      name: 'Die Cutting Accuracy',
      description: 'Check cut quality and accuracy',
      type: 'pass_fail',
      result: null,
      notes: ''
    }
  ];

  const [criteria, setCriteria] = useState(qualityCriteria);
  const [defectCategories, setDefectCategories] = useState([]);
  const [selectedDefects, setSelectedDefects] = useState([]);

  // Common defect categories
  const defectTypes = [
    { id: 'color_variation', name: 'Color Variation', severity: 'minor' },
    { id: 'misregistration', name: 'Misregistration', severity: 'major' },
    { id: 'scratches', name: 'Scratches', severity: 'minor' },
    { id: 'incomplete_cut', name: 'Incomplete Cut', severity: 'major' },
    { id: 'adhesive_failure', name: 'Adhesive Failure', severity: 'critical' },
    { id: 'contamination', name: 'Contamination', severity: 'major' },
    { id: 'dimension_error', name: 'Dimension Error', severity: 'major' }
  ];

  useEffect(() => {
    // Calculate overall pass rate
    const totalCriteria = criteria.filter(c => c.result !== null).length;
    const passedCriteria = criteria.filter(c => c.result === 'pass').length;

    if (totalCriteria > 0) {
      const passRate = (passedCriteria / totalCriteria) * 100;
      const overallPass = passRate >= 95; // 95% acceptance rate
      setInspectionData(prev => ({
        ...prev,
        overallResult: overallPass ? 'pass' : 'fail'
      }));
    }
  }, [criteria]);

  const handleCriteriaChange = (criteriaId, field, value) => {
    setCriteria(prev => prev.map(c =>
      c.id === criteriaId ? { ...c, [field]: value } : c
    ));
  };

  const handleMeasurementCheck = (criteriaId, actualValue) => {
    const criterion = criteria.find(c => c.id === criteriaId);
    if (!criterion) return;

    // Simple tolerance checking (could be more sophisticated)
    let result = 'fail';
    if (criterion.tolerance.includes('±')) {
      const tolerance = parseFloat(criterion.tolerance.replace('±', '').replace('mm', ''));
      const target = parseFloat(criterion.targetValue);
      const actual = parseFloat(actualValue);

      if (Math.abs(actual - target) <= tolerance) {
        result = 'pass';
      }
    } else if (criterion.tolerance.includes('>')) {
      const minValue = parseFloat(criterion.tolerance.replace('>', '').replace('N/25mm', ''));
      const actual = parseFloat(actualValue);

      if (actual >= minValue) {
        result = 'pass';
      }
    }

    handleCriteriaChange(criteriaId, 'actualValue', actualValue);
    handleCriteriaChange(criteriaId, 'result', result);
  };

  const handleDefectSelection = (defectId, quantity) => {
    setSelectedDefects(prev => {
      const existing = prev.find(d => d.id === defectId);
      if (existing) {
        return prev.map(d =>
          d.id === defectId ? { ...d, quantity } : d
        ).filter(d => d.quantity > 0);
      } else if (quantity > 0) {
        const defectType = defectTypes.find(d => d.id === defectId);
        return [...prev, { ...defectType, quantity }];
      }
      return prev;
    });
  };

  const handleCompleteInspection = () => {
    const allCriteriaChecked = criteria.every(c => c.result !== null);

    if (!allCriteriaChecked) {
      alert(t('qualityCheckTaskInterface.completeAllCriteria'));
      return;
    }

    onComplete({
      taskId: task.task_id,
      inspectionData,
      criteria,
      selectedDefects,
      completedAt: new Date().toISOString()
    });
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass': return '#28a745';
      case 'fail': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'minor': return '#ffc107';
      case 'major': return '#fd7e14';
      case 'critical': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="quality-check-interface">
      <div className="task-header">
        <h3>🔍 {task.task_name}</h3>
        <div className="task-type-badge quality">
          {t('taskTypes.qualityCheck')}
        </div>
        {inspectionData.overallResult && (
          <div
            className="overall-result"
            style={{ backgroundColor: getResultColor(inspectionData.overallResult) }}
          >
            {inspectionData.overallResult === 'pass' ? `✓ ${t('qualityCheckTaskInterface.result.pass').toUpperCase()}` : `✗ ${t('qualityCheckTaskInterface.result.fail').toUpperCase()}`}
          </div>
        )}
      </div>

      <div className="task-description">
        <p>{task.task_description}</p>
        {task.quality_checkpoints && (
          <div className="checkpoint-info">
            <h4>{t('qualityCheckTaskInterface.qualityCheckpoints')}</h4>
            <p>{task.quality_checkpoints}</p>
          </div>
        )}
      </div>

      {/* Sample Information */}
      <div className="qc-section">
        <h4>{t('qualityCheckTaskInterface.sampleInformation')} <small style={{ fontWeight: 'normal', fontSize: '12px', color: 'var(--text-secondary)' }}>({t('qualityCheckTaskInterface.unitsPieces')})</small></h4>
        <div className="sample-grid">
          <div className="sample-field">
            <label>{t('qualityCheckTaskInterface.sampleSize')}</label>
            <input
              type="number"
              value={inspectionData.sampleSize}
              onChange={(e) => setInspectionData(prev => ({
                ...prev,
                sampleSize: parseInt(e.target.value) || 0
              }))}
              className="sample-input"
              min="1"
              max="100"
              disabled={readOnly}
            />
          </div>
          <div className="sample-field">
            <label>{t('qualityCheckTaskInterface.inspectedQuantity')}</label>
            <input
              type="number"
              value={inspectionData.inspectedQuantity}
              onChange={(e) => setInspectionData(prev => ({
                ...prev,
                inspectedQuantity: parseInt(e.target.value) || 0
              }))}
              className="sample-input"
              min="0"
              disabled={readOnly}
            />
          </div>
          <div className="sample-field">
            <label>{t('qualityCheckTaskInterface.passed')}</label>
            <input
              type="number"
              value={inspectionData.passedQuantity}
              onChange={(e) => setInspectionData(prev => ({
                ...prev,
                passedQuantity: parseInt(e.target.value) || 0
              }))}
              className="sample-input pass"
              min="0"
              disabled={readOnly}
            />
          </div>
          <div className="sample-field">
            <label>{t('qualityCheckTaskInterface.failed')}</label>
            <input
              type="number"
              value={inspectionData.failedQuantity}
              onChange={(e) => setInspectionData(prev => ({
                ...prev,
                failedQuantity: parseInt(e.target.value) || 0
              }))}
              className="sample-input fail"
              min="0"
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      {/* Quality Criteria */}
      <div className="qc-section">
        <h4>{t('qualityCheckTaskInterface.qualityCriteria')}</h4>
        <div className="criteria-list">
          {criteria.map((criterion) => (
            <div key={criterion.id} className="criterion-item">
              <div className="criterion-header">
                <h5>{t(`qualityCheckTaskInterface.criteria.${criterion.id}.name`, { defaultValue: criterion.name })}</h5>
                <div
                  className="criterion-result"
                  style={{ backgroundColor: getResultColor(criterion.result) }}
                >
                  {criterion.result ? (criterion.result === 'pass' ? '✓' : '✗') : '?'}
                </div>
              </div>

              <p className="criterion-description">{t(`qualityCheckTaskInterface.criteria.${criterion.id}.description`, { defaultValue: criterion.description })}</p>

              {criterion.type === 'pass_fail' && !readOnly && (
                <div className="pass-fail-buttons">
                  <button
                    className={`btn btn-success ${criterion.result === 'pass' ? 'active' : ''}`}
                    onClick={() => handleCriteriaChange(criterion.id, 'result', 'pass')}
                  >
                    ✓ {t('qualityCheckTaskInterface.result.pass')}
                  </button>
                  <button
                    className={`btn btn-danger ${criterion.result === 'fail' ? 'active' : ''}`}
                    onClick={() => handleCriteriaChange(criterion.id, 'result', 'fail')}
                  >
                    ✗ {t('qualityCheckTaskInterface.result.fail')}
                  </button>
                </div>
              )}
              {criterion.type === 'pass_fail' && readOnly && criterion.result && (
                <div className="pass-fail-buttons">
                  <span className={`btn ${criterion.result === 'pass' ? 'btn-success active' : 'btn-danger active'}`}>
                    {criterion.result === 'pass' ? `✓ ${t('qualityCheckTaskInterface.result.pass')}` : `✗ ${t('qualityCheckTaskInterface.result.fail')}`}
                  </span>
                </div>
              )}

              {criterion.type === 'measurement' && (
                <div className="measurement-section">
                  <div className="measurement-fields">
                    <div className="field">
                      <label>{t('qualityCheckTaskInterface.target')}: {criterion.targetValue}</label>
                    </div>
                    <div className="field">
                      <label>{t('qualityCheckTaskInterface.tolerance')}: {criterion.tolerance}</label>
                    </div>
                    <div className="field">
                      <label>{t('qualityCheckTaskInterface.actualValue')}</label>
                      <input
                        type="number"
                        value={criterion.actualValue}
                        onChange={(e) => handleMeasurementCheck(criterion.id, e.target.value)}
                        placeholder={t('qualityCheckTaskInterface.enterMeasurement')}
                        className="measurement-input"
                        step="0.1"
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="criterion-notes">
                <textarea
                  value={criterion.notes}
                  onChange={(e) => handleCriteriaChange(criterion.id, 'notes', e.target.value)}
                  placeholder={t('qualityCheckTaskInterface.criterionNotesPlaceholder')}
                  className="notes-textarea"
                  rows="2"
                  disabled={readOnly}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Defect Recording */}
      <div className="qc-section">
        <h4>{t('qualityCheckTaskInterface.defectRecording')}</h4>
        <div className="defects-grid">
          {defectTypes.map((defect) => (
            <div key={defect.id} className="defect-item">
              <div className="defect-header">
                <span className="defect-name">{t(`qualityCheckTaskInterface.defects.${defect.id}`, { defaultValue: defect.name })}</span>
                <span
                  className="severity-badge"
                  style={{ backgroundColor: getSeverityColor(defect.severity) }}
                >
                  {t(`qualityCheckTaskInterface.severity.${defect.severity}`, { defaultValue: defect.severity })}
                </span>
              </div>
              <input
                type="number"
                value={selectedDefects.find(d => d.id === defect.id)?.quantity || ''}
                onChange={(e) => handleDefectSelection(defect.id, parseInt(e.target.value) || 0)}
                placeholder={t('qualityCheckTaskInterface.qty')}
                className="defect-quantity"
                min="0"
                disabled={readOnly}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Overall Notes */}
      <div className="qc-section">
        <h4>{t('qualityCheckTaskInterface.overallNotes')}</h4>
        <textarea
          value={inspectionData.notes}
          onChange={(e) => setInspectionData(prev => ({
            ...prev,
            notes: e.target.value
          }))}
          placeholder={t('qualityCheckTaskInterface.overallNotesPlaceholder')}
          className="overall-notes"
          rows="4"
          disabled={readOnly}
        />
      </div>

      {/* Action Buttons */}
      {!readOnly && <div className="action-section">
        <button
          className={`btn btn-primary btn-lg ${!criteria.every(c => c.result !== null) ? 'disabled' : ''}`}
          onClick={handleCompleteInspection}
          disabled={!criteria.every(c => c.result !== null)}
        >
          ✅ {t('qualityCheckTaskInterface.completeInspection')}
        </button>

        <div className="inspection-summary">
          <div className="summary-item">
            <span>{t('qualityCheckTaskInterface.criteriaChecked')}:</span>
            <span>{criteria.filter(c => c.result !== null).length} / {criteria.length}</span>
          </div>
          <div className="summary-item">
            <span>{t('qualityCheckTaskInterface.overallResult')}:</span>
            <span style={{ color: getResultColor(inspectionData.overallResult) }}>
              {(inspectionData.overallResult ? t(`qualityCheckTaskInterface.result.${inspectionData.overallResult}`) : t('qualityCheckTaskInterface.result.pending')).toUpperCase()}
            </span>
          </div>
        </div>
      </div>}

      <style jsx>{`
        .quality-check-interface {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #dc3545;
        }

        .task-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.3em;
        }

        .task-type-badge.quality {
          background: #dc3545;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85em;
          font-weight: bold;
          text-transform: uppercase;
        }

        .overall-result {
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-weight: bold;
          font-size: 0.9em;
        }

        .qc-section {
          margin-bottom: 25px;
          background: white;
          padding: 18px;
          border-radius: 6px;
          border-left: 4px solid #dc3545;
        }

        .qc-section h4 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 1.1em;
        }

        .sample-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
        }

        .sample-field {
          text-align: center;
        }

        .sample-field label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
          font-size: 14px;
        }

        .sample-input {
          width: 100%;
          padding: 10px;
          border: 2px solid #ddd;
          border-radius: 6px;
          font-size: 16px;
          font-weight: bold;
          text-align: center;
        }

        .sample-input.pass {
          border-color: #28a745;
          background: #f8fff9;
        }

        .sample-input.fail {
          border-color: #dc3545;
          background: #fff8f8;
        }

        .criteria-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .criterion-item {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .criterion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .criterion-header h5 {
          margin: 0;
          color: #2c3e50;
        }

        .criterion-result {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 16px;
        }

        .criterion-description {
          color: #6c757d;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .pass-fail-buttons {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
        }

        .measurement-section {
          margin-bottom: 15px;
        }

        .measurement-fields {
          display: grid;
          grid-template-columns: auto auto 1fr;
          gap: 15px;
          align-items: end;
        }

        .measurement-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .defects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .defect-item {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .defect-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .defect-name {
          font-weight: 500;
          color: #2c3e50;
          font-size: 14px;
        }

        .severity-badge {
          padding: 2px 8px;
          border-radius: 12px;
          color: white;
          font-size: 10px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .defect-quantity {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: center;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-lg {
          padding: 15px 30px;
          font-size: 16px;
        }

        .btn-success {
          background: #28a745;
          color: white;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn.active {
          box-shadow: 0 0 0 3px rgba(0,0,0,0.1);
          transform: scale(1.05);
        }

        .btn:hover:not(.disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .notes-textarea, .overall-notes {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          resize: vertical;
        }

        .overall-notes {
          min-height: 100px;
        }

        .action-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #dee2e6;
        }

        .inspection-summary {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          font-size: 14px;
        }

        .summary-item span:first-child {
          color: #6c757d;
        }

        .summary-item span:last-child {
          font-weight: 500;
        }

        .checkpoint-info {
          background: #e3f2fd;
          padding: 15px;
          border-radius: 6px;
          margin-top: 10px;
          border-left: 4px solid #2196f3;
        }

        .checkpoint-info h4 {
          margin-top: 0;
          color: #1976d2;
        }
      `}</style>
    </div>
  );
};

export default QualityCheckTaskInterface;